import React, { FC } from 'react';
import { IconCheck } from '../../utils/icons';
import { FeedbackForm, Step } from './types';

const INPUT =
    'w-full px-3.5 py-3 border border-border-token rounded-[10px] text-[14px] text-text-1 bg-surface-2 outline-none placeholder:text-muted focus:border-accent focus:bg-surface';
const FIELD_LABEL = 'block text-[13px] font-semibold text-text-1 mb-2';

interface Props {
    form: FeedbackForm;
    steps: Step[];
    stepDone: boolean[];
    name: string;
    email: string;
    askForContact: boolean;
    onSelectStep: (index: number) => void;
    onName: (value: string) => void;
    onEmail: (value: string) => void;
}

export const SubmitStep: FC<Props> = ({
    form,
    steps,
    stepDone,
    name,
    email,
    askForContact,
    onSelectStep,
    onName,
    onEmail,
}) => {
    const labelFor = (entry: Step): string => {
        if (entry.kind === 'order') return 'Your experience';
        return form.projects[entry.projectIndex as number].title;
    };

    return (
        <>
            <div className="pb-5 mb-[22px] border-b border-border-token">
                <span className="inline-flex items-center gap-[5px] text-[11px] font-bold px-2.5 py-[3px] rounded-md uppercase tracking-[0.4px] text-up bg-up-bg">
                    {/* currentColor: the stroke lands on an SVG presentation
                        attribute, which cannot resolve var(). */}
                    <IconCheck width={14} height={14} stroke="currentColor" />
                    Almost there
                </span>
                <h2 className="text-[20px] font-bold text-text-1 mt-2.5 mb-0 tracking-[-0.2px]">
                    {form.submitted ? 'Review & update' : 'Review & submit'}
                </h2>
                {form.submitted && (
                    <p className="text-[13px] text-text-2 mt-1">
                        You have already sent this feedback. Change anything you like
                        and send it again.
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-2 mb-[22px]">
                {steps.slice(0, -1).map((entry, index) => (
                    <button
                        type="button"
                        key={entry.label}
                        className="flex items-center gap-[11px] px-3.5 py-3 border border-border-token rounded-[10px] cursor-pointer transition-all w-full text-left bg-transparent hover:border-accent hover:bg-surface-2"
                        onClick={() => onSelectStep(index)}
                    >
                        {/* Complete/incomplete status hues stay literal here and
                            below: --up/--down are the darker text values and
                            would visibly recolor these light-mode states. */}
                        <span
                            className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[12px] font-bold shrink-0 ${
                                stepDone[index]
                                    ? 'bg-[#1C7A5A] text-white'
                                    : 'bg-track text-muted'
                            }`}
                        >
                            {stepDone[index] ? (
                                <IconCheck width={14} height={14} stroke="#FFF" />
                            ) : (
                                '!'
                            )}
                        </span>
                        <span className="flex-1 text-[14px] font-semibold text-text-1">
                            {labelFor(entry)}
                        </span>
                        <span
                            className={`text-[12px] font-semibold ${
                                stepDone[index]
                                    ? 'text-[#1C7A5A]'
                                    : 'text-[#C0392B]'
                            }`}
                        >
                            {stepDone[index] ? 'Complete' : 'Incomplete'}
                        </span>
                    </button>
                ))}
            </div>

            {askForContact && (
                <div className="grid grid-cols-2 gap-4 max-[560px]:grid-cols-1">
                    <div>
                        <label className={FIELD_LABEL}>Your name</label>
                        <input
                            className={INPUT}
                            placeholder="Jane Doe"
                            value={name}
                            onChange={(e) => onName(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className={FIELD_LABEL}>Your email</label>
                        <input
                            className={INPUT}
                            placeholder="jane@brand.com"
                            value={email}
                            onChange={(e) => onEmail(e.target.value)}
                        />
                    </div>
                </div>
            )}
        </>
    );
};
